import Vue from "vue";
import { io } from "socket.io-client";

import authApi from "@/api/auth";
import { EventBus } from "@/eventBus";
import apiContour, { API_CONTOUR_ORCHESTRATOR } from "@/services/apiContour";
import {
  EMPLOYEE_STATE_INACTIVE,
  EMPLOYEE_STATE_READY,
  EMPLOYEE_STATE_TRAINING,
} from "@/constants/employee";
import userLogger from "@/utils/userLogger";
import { createLogger } from "@/utils/logger";

const SOCKET_HOST = typeof process.env.VUE_APP_SOCKET_HOST !== "undefined"
  ? process.env.VUE_APP_SOCKET_HOST
  : "https://unicall.io";
const SOCKET_PATH = typeof process.env.VUE_APP_SOCKET_PATH !== "undefined"
  ? process.env.VUE_APP_SOCKET_PATH
  : "/socket/socket.io/";
const logger = createLogger("apiSocket");
const STATUS_EMIT_DEDUP_WINDOW_MS = 500;
const SELF_SYNC_FRESH_WINDOW_MS = 1500;
const SOCKET_TAB_ID_KEY = "api_socket_tab_id";
const SESSION_CONFLICT_PAGE_PATH = "/session-conflict.html";
const PHONE_CALL_LOG_QUEUE_MAX = 50;
const PHONE_CALL_LOG_ACK_TIMEOUT_MS = 5000;
const PHONE_CALL_LOG_RETRY_DELAY_MS = 2000;

class ApiSocketService {
  constructor() {
    this.socket = null;
    this.root = null;
    this.store = null;
    this.eventBusHandlers = null;
    this.isInitialized = false;
    this.isUserLoggerInitialized = false;
    this.pendingReload = false;
    this.pendingApiContour = apiContour.getPendingContour();
    this.pendingSipUpdate = null;
    this.userAttributesUnwatch = null;
    this.phoneActivityUnwatch = null;
    this.lastStatusEmission = null;
    this.selfSyncPromise = null;
    this.lastSelfSyncAt = 0;
    this.lifecycleHandlersRegistered = false;
    this.phoneCallLogQueue = [];
    this.phoneCallLogInFlight = null;
    this.phoneCallLogFlushTimer = null;
    this.sessionConflictLocked = false;
    this.connectAttemptInFlight = false;
  }

  init({ root, store }) {
    this.root = root;
    this.store = store;

    if (this.isInitialized) {
      return;
    }

    this.registerEventBusHandlers();
    this.registerUserAttributesWatch();
    this.registerPhoneActivityWatch();
    this.registerLifecycleEventHandlers();
    this.isInitialized = true;
  }

  setConnected(connected) {
    if (!this.store) return;

    this.store.commit("SET_API_SOCKET_CONNECTED", connected);
  }

  syncAuthHeaders(headers = {}) {
    const nextToken = headers["x-auth-token"];
    const nextExpiration = headers["x-api-token-expires"];
    const previousToken = window.localStorage.getItem("token");

    if (nextToken) {
      Vue.auth.setToken(nextToken, nextExpiration);
      if (previousToken && previousToken !== nextToken) {
        EventBus.$emit("auth-token-updated", nextToken);
      }
      return;
    }

    Vue.auth.updateTokenExpiration(nextExpiration);
  }

  applyBackendTimeSync(time, headers = {}) {
    if (this.sessionConflictLocked) {
      return null;
    }

    if (!time || !this.store) {
      return null;
    }

    this.store.commit("SET_TIME_DATA", time);
    window.sessionStorage.setItem("state", time.state);

    if (time.state === EMPLOYEE_STATE_TRAINING && typeof time.trainingId !== "undefined") {
      window.sessionStorage.setItem("trainingId", time.trainingId);
    } else {
      window.sessionStorage.removeItem("trainingId");
    }

    this.syncAuthHeaders(headers);
    this.store.dispatch("toggleKeepAlive", time.state);

    return time;
  }

  async syncSessionState({
    forceEmit = false,
    minFreshMs = 0,
    reason = "manual",
  } = {}) {
    if (this.sessionConflictLocked) {
      this.disconnect();
      return null;
    }

    if (!Vue.auth.isAuthenticated()) {
      this.disconnect();
      return null;
    }

    const now = Date.now();
    if (this.selfSyncPromise) {
      const pendingResult = await this.selfSyncPromise;
      if (forceEmit && this.socket?.connected) {
        this.emitCurrentState(null, {
          force: true,
          trigger: `${reason}.after-pending-sync`,
        });
      }

      return pendingResult;
    }

    if (minFreshMs > 0 && now - this.lastSelfSyncAt < minFreshMs) {
      if (forceEmit && this.socket?.connected) {
        this.emitCurrentState(null, {
          force: true,
          trigger: `${reason}.cached`,
        });
      }

      return this.store?.state?.auth?.time || null;
    }

    this.selfSyncPromise = authApi.getTime()
      .then(({ data: { data: { time } }, headers, status }) => {
        if (status !== 200) {
          return null;
        }

        const syncedTime = this.applyBackendTimeSync(time, headers);
        this.lastSelfSyncAt = Date.now();

        if (forceEmit && this.socket?.connected) {
          this.emitCurrentState(null, {
            force: true,
            trigger: `${reason}.force-reemit`,
          });
        }

        return syncedTime;
      })
      .catch((error) => {
        logger.warn("Session sync failed", {
          reason,
          status: error?.response?.status || null,
        });
        throw error;
      })
      .finally(() => {
        this.selfSyncPromise = null;
      });

    return this.selfSyncPromise;
  }

  mapSessionStateToStatus(state) {
    switch (+state) {
      case 0:
        return "online";
      case 1:
        return "is-ready";
      case 2:
        return "training";
      default:
        return "online";
    }
  }

  getBaseEmployeeState() {
    const authState = this.store?.state?.auth?.time?.state;

    if (authState !== null && typeof authState !== "undefined" && authState !== "") {
      return +authState;
    }

    const storedState = window.sessionStorage.getItem("state");
    if (storedState === null || storedState === "") {
      return null;
    }

    return +storedState;
  }

  resolveBaseSocketStatus(targetState = null) {
    if (targetState) {
      return targetState;
    }

    return this.mapSessionStateToStatus(this.getBaseEmployeeState());
  }

  currentState() {
    const userState = this.resolveBaseSocketStatus();

    if (userState !== "is-ready") {
      return userState;
    }

    if (this.store && this.store.state.order.order) {
      return "opened-order";
    }

    return userState;
  }

  getCurrentOrderId() {
    return this.store?.state?.order?.order?.order?.id || null;
  }

  hasActivePhoneCall() {
    return Boolean(this.store?.getters?.getPhoneHasActiveCall);
  }

  canFetchAssignedOrder() {
    const currentState = this.getBaseEmployeeState();
    const hasOpenedOrder = Boolean(this.store?.state?.order?.order);

    return currentState === EMPLOYEE_STATE_READY
      && !hasOpenedOrder
      && !this.hasActivePhoneCall();
  }

  resolveCurrentStatusSnapshot(targetState = null) {
    const baseStatus = this.resolveBaseSocketStatus(targetState);
    const orderId = this.getCurrentOrderId();
    const isCalling = this.hasActivePhoneCall();

    if (baseStatus !== "is-ready") {
      return {
        status: baseStatus,
        data: {},
      };
    }

    if (orderId && isCalling) {
      return {
        status: "calling",
        data: { orderId },
      };
    }

    if (orderId) {
      return {
        status: "opened-order",
        data: { orderId },
      };
    }

    return {
      status: baseStatus,
      data: {},
    };
  }

  resetStatusEmissionDedup() {
    this.lastStatusEmission = null;
  }

  buildStatusFingerprint(status, data = {}) {
    return JSON.stringify({
      status,
      data: data || {},
    });
  }

  shouldSkipDuplicateStatus(status, data = {}, meta = {}) {
    if (meta.force) {
      return false;
    }

    const now = Date.now();
    const fingerprint = this.buildStatusFingerprint(status, data);
    const previousStatusEmission = this.lastStatusEmission;

    if (
      previousStatusEmission
      && previousStatusEmission.fingerprint === fingerprint
      && now - previousStatusEmission.at < STATUS_EMIT_DEDUP_WINDOW_MS
    ) {
      return true;
    }

    this.lastStatusEmission = {
      at: now,
      fingerprint,
    };
    return false;
  }

  emitCurrentState(targetState = null, meta = {}) {
    const { status, data } = this.resolveCurrentStatusSnapshot(targetState);
    this.emitStatusUpdate(status, data, meta);
  }

  emitStatusUpdate(status, data = {}, meta = {}) {
    if (!this.socket || !this.socket.connected) return;

    if (this.shouldSkipDuplicateStatus(status, data, meta)) {
      logger.debug("Skip duplicate status update", {
        data,
        status,
        trigger: meta.trigger || null,
      });
      return;
    }

    this.socket.emit("statusUpdate", status, data, {
      clientTs: new Date().toISOString(),
      trigger: meta.trigger || null,
    });
  }

  emitCabinetLog(data = {}) {
    if (!this.socket || !this.socket.connected) return;

    this.socket.emit("cabinet-log", data);
  }

  emitLifecycleCabinetLog(eventName, payload = {}) {
    this.emitCabinetLog({
      createdAt: new Date().toISOString(),
      eventName,
      orderId: this.getCurrentOrderId(),
      phoneState: this.store?.state?.phone?.status || null,
      source: "api-socket",
      lifecycle: {
        hasActivePhoneCall: this.hasActivePhoneCall(),
        isOrderOpened: Boolean(this.store?.state?.order?.order),
        pendingReload: Boolean(this.pendingReload),
        socketConnected: Boolean(this.socket && this.socket.connected),
      },
      ...payload,
    });
  }

  emitSendActionLogData(type, data = {}) {
    if (!this.socket || !this.socket.connected) return;

    this.socket.emit("logData", type, data);
  }

  isConnected() {
    return Boolean(this.socket && this.socket.connected);
  }

  enqueuePhoneCallLog(payload = {}, handlers = {}) {
    if (this.sessionConflictLocked) {
      if (typeof handlers.onRejected === "function") {
        handlers.onRejected({ reason: "session_conflict_locked" });
      }
      return;
    }

    const queuedPayload = {
      ...payload,
      client_event_created_at: payload.client_event_created_at || Math.floor(Date.now() / 1000),
      client_event_id: payload.client_event_id || this.generateEventId(),
    };

    if (this.phoneCallLogQueue.length >= PHONE_CALL_LOG_QUEUE_MAX) {
      const dropped = this.phoneCallLogQueue.shift();
      logger.warn("Phone call log queue overflow, dropped oldest item", {
        droppedType: dropped?.payload?.type || null,
        orderId: dropped?.payload?.order_id || null,
      });
    }

    this.phoneCallLogQueue.push({
      attempts: 0,
      enqueuedAt: Date.now(),
      handlers,
      payload: queuedPayload,
    });
    this.flushPhoneCallLogQueue();
  }

  schedulePhoneCallLogFlush() {
    if (this.sessionConflictLocked) {
      return;
    }

    if (this.phoneCallLogFlushTimer) {
      return;
    }

    this.phoneCallLogFlushTimer = setTimeout(() => {
      this.phoneCallLogFlushTimer = null;
      this.flushPhoneCallLogQueue();
    }, PHONE_CALL_LOG_RETRY_DELAY_MS);
  }

  flushPhoneCallLogQueue() {
    if (this.sessionConflictLocked) {
      return;
    }

    if (this.phoneCallLogInFlight || !this.isConnected()) {
      return;
    }

    const item = this.phoneCallLogQueue.shift();
    if (!item) {
      return;
    }

    item.attempts += 1;
    this.phoneCallLogInFlight = item;

    this.socket
      .timeout(PHONE_CALL_LOG_ACK_TIMEOUT_MS)
      .emit("phone-call-log", item.payload, (error, response = {}) => {
        this.phoneCallLogInFlight = null;

        if (this.sessionConflictLocked) {
          return;
        }

        if (error || response.ok !== true) {
          const retryable = error || response.retryable !== false;
          if (retryable) {
            this.phoneCallLogQueue.unshift(item);
            this.schedulePhoneCallLogFlush();
            return;
          }

          logger.warn("Phone call log rejected by socket server", {
            reason: response.reason || null,
            type: item.payload?.type || null,
          });
          if (typeof item.handlers.onRejected === "function") {
            item.handlers.onRejected(response);
          }
          this.flushPhoneCallLogQueue();
          return;
        }

        if (typeof item.handlers.onAck === "function") {
          item.handlers.onAck(response);
        }
        this.flushPhoneCallLogQueue();
      });
  }

  clearPhoneCallLogQueue() {
    if (this.phoneCallLogFlushTimer) {
      clearTimeout(this.phoneCallLogFlushTimer);
      this.phoneCallLogFlushTimer = null;
    }

    this.phoneCallLogQueue = [];
    this.phoneCallLogInFlight = null;
  }

  redirectToSessionConflictPage() {
    window.location.replace(SESSION_CONFLICT_PAGE_PATH);
  }

  normalizeSipDataPayload(payload) {
    if (!payload) {
      return null;
    }

    if (payload.sip_data) {
      return payload.sip_data;
    }

    if (payload.data) {
      return payload.data;
    }

    return payload;
  }

  updateUserAttributes(payload) {
    if (!this.store) {
      return;
    }

    this.store.commit("UPDATE_USER_ATTRIBUTES_DATA", payload);
  }

  handleReloadCommand() {
    const isOrderOpened = !!this.store?.state?.order?.order;
    const hasActivePhoneCall = this.hasActivePhoneCall();

    if (isOrderOpened || hasActivePhoneCall) {
      this.pendingReload = true;
      this.emitLifecycleCabinetLog("socket.reload.deferred", {
        reason: "command.reload",
      });
      return;
    }

    this.emitLifecycleCabinetLog("socket.reload.executing.command", {
      reason: "command.reload",
    });
    window.location.reload();
  }

  checkPendingReload() {
    if (!this.pendingReload) {
      return;
    }

    const isOrderOpened = !!this.store?.state?.order?.order;
    if (isOrderOpened || this.hasActivePhoneCall()) {
      return;
    }

    this.emitLifecycleCabinetLog("socket.reload.executing.pending", {
      reason: "pending_reload",
    });
    this.pendingReload = false;
    window.location.reload();
  }

  applySipDataUpdate(payload) {
    const sipData = this.normalizeSipDataPayload(payload);
    if (!sipData) {
      return;
    }

    this.updateUserAttributes({
      sip_data: sipData,
    });
  }

  handleUpdateSipDataCommand(data) {
    if (this.hasActivePhoneCall()) {
      this.pendingSipUpdate = data;
      return;
    }

    this.applySipDataUpdate(data);
  }

  checkPendingSipUpdate() {
    if (!this.pendingSipUpdate || this.hasActivePhoneCall()) {
      return;
    }

    this.applySipDataUpdate(this.pendingSipUpdate);
    this.pendingSipUpdate = null;
  }

  handleActionsTrackingCommand(state) {
    this.updateUserAttributes({
      actions_tracking: Boolean(state),
    });
  }

  handleFetchOrderCommand() {
    if (!this.canFetchAssignedOrder()) {
      logger.debug("Skip fetch-order command: operator is not waiting for order");
      return;
    }

    this.store?.dispatch("fetchOrder").catch(() => {});
  }

  recoverAssignedOrder() {
    if (apiContour.getCurrentContour() !== API_CONTOUR_ORCHESTRATOR) {
      return;
    }

    if (!this.canFetchAssignedOrder()) {
      return;
    }

    this.store?.dispatch("fetchOrder").catch(() => {});
  }

  handleCloseOrderCommand(data = {}) {
    const currentOrderId = Number(this.getCurrentOrderId()) || null;
    const canceledOrderId = Number(data?.orderId) || null;

    if (currentOrderId && canceledOrderId && currentOrderId !== canceledOrderId) {
      logger.debug("Skip close-order command for another active order", {
        canceledOrderId,
        currentOrderId,
      });
      return;
    }

    EventBus.$emit("clear-phone-data", true);
    EventBus.$emit("reset-order-timer", true);
    this.store?.commit("RESET_PRODUCTS_AND_SETS_FORM");
    this.store?.commit("RESET_ORDER_ERRORS_RULES_DATA");
    this.store?.commit("RESET_ORDER_ERRORS_DATA");
    this.store?.commit("RESET_ORDER_DATA");
    this.store?.commit("RESET_SCRIPTS_DATA");
    this.store?.commit("RESET_SERVER_ERRORS_DATA");
    this.store?.commit("SET_IS_ORDER_OPENED", false);
    this.store?.commit("SET_REFETCH_ORDER_DATA", false);
    this.store?.commit("SET_IS_LOADING_ORDER_DATA", false);

    this.store?.dispatch("changeReady", {
      state: EMPLOYEE_STATE_INACTIVE,
      target: "task_cancel",
    }).catch(() => {});

    this.emitLifecycleCabinetLog("socket.order.closed", {
      canceledOrderId,
      reason: data?.reason || null,
      taskId: data?.taskId || null,
    });
  }

  handleSwitchApiContourCommand(data = {}) {
    const requestedContour = apiContour.normalize(data?.contour);
    const currentContour = apiContour.getCurrentContour();

    if (!requestedContour) {
      logger.warn("API contour switch rejected", {
        contour: data?.contour || null,
        reason: "unsupported_contour",
      });
      this.emitLifecycleCabinetLog("socket.api_contour.rejected", {
        contour: data?.contour || null,
        reason: "unsupported_contour",
      });
      return;
    }

    if (requestedContour === currentContour) {
      this.pendingApiContour = null;
      apiContour.clearPending();
      this.emitLifecycleCabinetLog("socket.api_contour.unchanged", {
        contour: requestedContour,
        reason: data?.reason || null,
      });
      return;
    }

    const isOrderOpened = !!this.store?.state?.order?.order;
    if (isOrderOpened || this.hasActivePhoneCall()) {
      this.pendingApiContour = requestedContour;
      apiContour.defer(requestedContour);
      this.emitLifecycleCabinetLog("socket.api_contour.deferred", {
        contour: requestedContour,
        reason: data?.reason || null,
      });
      return;
    }

    this.applyApiContour(requestedContour, data?.reason || null);
  }

  applyApiContour(contour, reason = null) {
    const result = apiContour.activate(contour);
    if (!result.ok) {
      logger.warn("API contour switch rejected", {
        contour,
        reason: result.reason,
      });
      this.emitLifecycleCabinetLog("socket.api_contour.rejected", {
        contour,
        reason: result.reason,
      });
      return false;
    }

    this.pendingApiContour = null;
    apiContour.clearPending();
    this.pendingReload = false;
    this.emitLifecycleCabinetLog("socket.api_contour.applied", {
      contour: result.contour,
      reason,
    });
    window.location.reload();

    return true;
  }

  checkPendingApiContour() {
    if (!this.pendingApiContour) {
      return false;
    }

    const isOrderOpened = !!this.store?.state?.order?.order;
    if (isOrderOpened || this.hasActivePhoneCall()) {
      return false;
    }

    return this.applyApiContour(this.pendingApiContour, "pending_switch");
  }

  handleResyncStateCommand(commandData = {}) {
    const commandReason = commandData?.reason || "default";
    const trigger = `command.resync-state.${commandReason}`;

    this.syncSessionState({
      minFreshMs: SELF_SYNC_FRESH_WINDOW_MS,
      reason: trigger,
    }).then(() => {
      const currentSnapshot = this.resolveCurrentStatusSnapshot();

      if (
        commandReason === "ws.status_update.invalid_state"
        && currentSnapshot.status === commandData?.requestedStatus
      ) {
        return;
      }

      this.emitStatusUpdate(currentSnapshot.status, currentSnapshot.data, {
        trigger,
      });
    }).catch(() => {});
  }

  handleForceLogoutCommand(data = {}) {
    logger.info("Force logout command received", data);
    this.emitLifecycleCabinetLog("socket.force_logout.received", {
      commandData: data || {},
      reason: data?.reason || null,
    });
    this.emitLifecycleCabinetLog("socket.reload.executing.force_logout", {
      reason: data?.reason || null,
    });
    this.disconnect();
    Vue.onlineToken?.destroy?.();
    Vue.auth.destroyToken("ws_force_logout", {
      commandReason: data?.reason || null,
    });
    window.location.reload();
  }

  initUserLogger() {
    const userAttributes = this.store?.getters?.getUserAttributes;
    const actionsTracking = Boolean(userAttributes?.actions_tracking);

    if (!actionsTracking || !this.socket?.connected || this.isUserLoggerInitialized) {
      return;
    }

    userLogger.install(this.store);
    this.isUserLoggerInitialized = true;
  }

  removeUserLogger() {
    if (!this.isUserLoggerInitialized) {
      return;
    }

    userLogger.remove();
    this.isUserLoggerInitialized = false;
  }

  registerUserAttributesWatch() {
    if (!this.store || this.userAttributesUnwatch) {
      return;
    }

    this.userAttributesUnwatch = this.store.watch(
      () => Boolean(this.store.getters.getUserAttributes && this.store.getters.getUserAttributes.actions_tracking),
      (actionsTracking) => {
        if (actionsTracking) {
          this.initUserLogger();
          return;
        }

        this.removeUserLogger();
      },
      {
        immediate: true,
      },
    );
  }

  registerPhoneActivityWatch() {
    if (!this.store || this.phoneActivityUnwatch) {
      return;
    }

    this.phoneActivityUnwatch = this.store.watch(
      () => Boolean(this.store.getters.getPhoneHasActiveCall),
      (hasActiveCall, previousValue) => {
        if (hasActiveCall === previousValue) {
          return;
        }

        this.emitCurrentState(null, {
          trigger: "watch.phone-has-active-call",
        });

        if (!hasActiveCall) {
          if (!this.checkPendingApiContour()) {
            this.checkPendingReload();
          }
          this.checkPendingSipUpdate();
        }
      },
    );
  }

  registerLifecycleEventHandlers() {
    if (this.lifecycleHandlersRegistered) {
      return;
    }

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      this.handleLifecycleSync("visibilitychange");
    });

    window.addEventListener("pageshow", () => {
      this.handleLifecycleSync("pageshow");
    });

    window.addEventListener("focus", () => {
      this.handleLifecycleSync("focus");
    });

    window.addEventListener("online", () => {
      this.handleLifecycleSync("online");
    });

    this.lifecycleHandlersRegistered = true;
  }

  handleLifecycleSync(reason) {
    if (this.sessionConflictLocked) {
      this.disconnect();
      return;
    }

    if (!Vue.auth.isAuthenticated()) {
      return;
    }

    this.syncSessionState({
      forceEmit: true,
      minFreshMs: SELF_SYNC_FRESH_WINDOW_MS,
      reason: `lifecycle.${reason}`,
    }).then(() => {
      if (this.socket && !this.socket.connected) {
        this.disconnect();
        this.connectSocket();
        return;
      }

      if (!this.socket) {
        this.connectSocket();
      }
    }).catch(() => {});
  }

  hasValidUserId(userId) {
    if (userId === null || typeof userId === "undefined") {
      return false;
    }

    const normalizedUserId = String(userId).trim().toLowerCase();

    return normalizedUserId !== "" && normalizedUserId !== "null" && normalizedUserId !== "undefined";
  }

  generateTabId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  generateEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    return `event-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  getOrCreateTabId() {
    const existingTabId = window.sessionStorage.getItem(SOCKET_TAB_ID_KEY);
    if (existingTabId) {
      return existingTabId;
    }

    const newTabId = this.generateTabId();
    window.sessionStorage.setItem(SOCKET_TAB_ID_KEY, newTabId);
    return newTabId;
  }

  async connect() {
    if (this.sessionConflictLocked) {
      this.disconnect();
      return;
    }

    if (this.socket || !Vue.auth.isAuthenticated()) {
      return;
    }

    try {
      await this.syncSessionState({
        minFreshMs: SELF_SYNC_FRESH_WINDOW_MS,
        reason: "socket.connect",
      });
    } catch (error) {
      return;
    }

    if (this.socket || !Vue.auth.isAuthenticated()) {
      return;
    }

    this.connectSocket();
  }

  async connectSocket() {
    if (this.sessionConflictLocked) {
      this.disconnect();
      return;
    }

    if (this.socket || this.connectAttemptInFlight || !Vue.auth.isAuthenticated()) {
      return;
    }

    this.connectAttemptInFlight = true;

    try {
      const expirationRaw = window.localStorage.getItem("expiration");
      const expiration = expirationRaw ? Number(expirationRaw) || 0 : 0;
      const now = Math.round(Date.now() / 1000);

      if (expiration && now > expiration) {
        await Vue.auth.requestExpirationRecheck({
          expiration,
          now,
          token: window.localStorage.getItem("token"),
        });
      }

      const token = Vue.auth.getToken();
      const user = Vue.auth.getUser();
      if (!token || !user || !user.username || this.socket) {
        return;
      }

      const initialStatusSnapshot = this.resolveCurrentStatusSnapshot();
      const query = {
        token,
        tab_id: this.getOrCreateTabId(),
        username: user.username,
        state: initialStatusSnapshot.status,
      };

      if (this.hasValidUserId(user.user_id)) {
        query.user_id = String(user.user_id);
      }

      if (Object.keys(initialStatusSnapshot.data).length > 0) {
        query.status_data = JSON.stringify(initialStatusSnapshot.data);
      }

      this.socket = io(SOCKET_HOST, {
        path: SOCKET_PATH,
        query,
        transports: ["websocket"],
      });

      this.socket.on("connect", () => {
        this.resetStatusEmissionDedup();
        this.setConnected(true);
        this.initUserLogger();
        this.emitCurrentState(null, {
          force: true,
          trigger: "socket.connect",
        });
        this.recoverAssignedOrder();
        this.flushPhoneCallLogQueue();
        logger.info("Connected");
      });

      this.socket.on("disconnect", (reason) => {
        this.resetStatusEmissionDedup();
        this.setConnected(false);
        logger.info("Disconnected", reason);
      });

      this.socket.on("clean old session", () => {
        this.handleSessionConflict();
      });

      this.socket.on("initialTime", (data) => {
        logger.debug("initialTime", data);
      });

      this.socket.on("command", (command, data) => {
        switch (command) {
          case "reload":
            this.handleReloadCommand();
            break;
          case "resync-state":
            this.handleResyncStateCommand(data);
            break;
          case "force-logout":
            this.handleForceLogoutCommand(data);
            break;
          case "update-sip-data":
            this.handleUpdateSipDataCommand(data);
            break;
          case "actions_tracking":
            this.handleActionsTrackingCommand(data);
            break;
          case "fetch-order":
            this.handleFetchOrderCommand();
            break;
          case "close-order":
            this.handleCloseOrderCommand(data);
            break;
          case "switch-api-contour":
            this.handleSwitchApiContourCommand(data);
            break;
          default:
            logger.debug("Unknown command", command, data);
        }
      });

      this.socket.on("connect_error", (error) => {
        logger.warn("Socket connect error", {
          message: error?.message || null,
        });
      });
    } finally {
      this.connectAttemptInFlight = false;
    }
  }

  disconnect() {
    if (!this.socket) {
      this.setConnected(false);
      return;
    }

    this.socket.off();
    this.socket.disconnect();
    this.socket = null;
    this.resetStatusEmissionDedup();
    this.setConnected(false);
    this.removeUserLogger();
  }

  syncConnection() {
    if (this.sessionConflictLocked) {
      this.disconnect();
      return;
    }

    if (Vue.auth.isAuthenticated()) {
      this.connect().catch(() => {});
      return;
    }

    this.disconnect();
  }

  handleSessionConflict() {
    this.sessionConflictLocked = true;
    this.emitLifecycleCabinetLog("socket.session_conflict.received");
    EventBus.$emit("destroy-app-instance");
    this.store?.dispatch("toggleKeepAlive", EMPLOYEE_STATE_INACTIVE).catch(() => {});
    this.store?.dispatch("clearGetOrderInterval").catch(() => {});
    this.clearPhoneCallLogQueue();
    this.pendingReload = false;
    this.pendingApiContour = null;
    apiContour.clearPending();
    this.pendingSipUpdate = null;
    this.selfSyncPromise = null;
    window.sessionStorage.removeItem(SOCKET_TAB_ID_KEY);
    this.disconnect();

    this.redirectToSessionConflictPage();
  }

  registerEventBusHandlers() {
    this.eventBusHandlers = {
      openedOrder: () => {
        this.emitCurrentState(null, {
          trigger: "eventBus.opened-order",
        });
      },
      changeReady: (nextState) => {
        const targetStatus = typeof nextState === "undefined"
          ? null
          : this.mapSessionStateToStatus(nextState);
        this.emitCurrentState(targetStatus, {
          force: true,
          trigger: "eventBus.change-ready",
        });
      },
      savedOrder: () => {
        this.emitCurrentState(null, {
          trigger: "eventBus.saved-order",
        });
        if (!this.checkPendingApiContour()) {
          this.checkPendingReload();
        }
      },
      orderUnavailable: () => {
        this.checkPendingApiContour();
      },
      authTokenUpdated: () => {
        if (!Vue.auth.isAuthenticated() || !this.socket) {
          return;
        }

        this.disconnect();
        this.connect().catch(() => {});
      },
      logoutUser: () => {
        this.disconnect();
      },
      cabinetLog: (data) => {
        this.emitCabinetLog(data);
      },
      sendLogData: (type, data) => {
        this.emitSendActionLogData(type, data);
      },
    };

    EventBus.$on("opened-order", this.eventBusHandlers.openedOrder);
    EventBus.$on("change-ready", this.eventBusHandlers.changeReady);
    EventBus.$on("saved-order", this.eventBusHandlers.savedOrder);
    EventBus.$on("order-unavailable", this.eventBusHandlers.orderUnavailable);
    EventBus.$on("auth-token-updated", this.eventBusHandlers.authTokenUpdated);
    EventBus.$on("logout-user", this.eventBusHandlers.logoutUser);
    EventBus.$on("cabinet-log", this.eventBusHandlers.cabinetLog);
    EventBus.$on("send-log-data", this.eventBusHandlers.sendLogData);
  }
}

export { ApiSocketService };
export default new ApiSocketService();
